import { useMemo } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useConsolidatedKpis } from '@/hooks/useConsolidatedKpis';
import { formatCurrency } from '@/utils/formatters';
import { PendingTransfersWidget } from '@/components/Stock/PendingTransfersWidget';
import { GroupBalancePanel } from '@/components/Finance/GroupBalancePanel';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export function ConsolidatedGroupDashboard() {
  const startDate = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const kpiQuery = useConsolidatedKpis(startDate, endDate, { enabled: true });

  const chartData = useMemo(() => {
    const rows = kpiQuery.data?.companies ?? [];
    return rows.map((c) => ({
      name: c.name.length > 22 ? `${c.name.slice(0, 20)}…` : c.name,
      Faturamento: c.faturamento,
      CMV: c.cmv,
    }));
  }, [kpiQuery.data?.companies]);

  const pendingRateios = kpiQuery.data?.pending_rateios ?? 0;

  return (
    <div className="space-y-6">
      {kpiQuery.isError && (
        <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          Não foi possível carregar os KPIs consolidados. Aplique no Supabase o arquivo{' '}
          <code className="text-xs">docs/sql/sprint05_consolidated_kpis.sql</code> e verifique permissões.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Período: {format(startOfMonth(new Date()), 'dd/MM/yyyy')} — {format(endOfMonth(new Date()), 'dd/MM/yyyy')}
        </p>
        {kpiQuery.isLoading ? (
          <Skeleton className="h-6 w-24 rounded-full" />
        ) : (
          <Badge variant="secondary" className="text-sm tabular-nums">
            Rateios pendentes: {pendingRateios}
          </Badge>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="min-h-[120px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Faturamento total (grupo)</CardTitle>
          </CardHeader>
          <CardContent>
            {kpiQuery.isLoading ? (
              <Skeleton className="h-9 w-36" />
            ) : (
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(kpiQuery.data?.faturamento_total ?? 0)}</p>
            )}
          </CardContent>
        </Card>
        <Card className="min-h-[120px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">CMV total (grupo)</CardTitle>
          </CardHeader>
          <CardContent>
            {kpiQuery.isLoading ? (
              <Skeleton className="h-9 w-36" />
            ) : (
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(kpiQuery.data?.cmv_total ?? 0)}</p>
            )}
          </CardContent>
        </Card>
        <Card className="min-h-[120px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Margem bruta</CardTitle>
          </CardHeader>
          <CardContent>
            {kpiQuery.isLoading ? (
              <Skeleton className="h-9 w-20" />
            ) : (
              <p className="text-2xl font-bold tabular-nums">
                {(kpiQuery.data?.margem_bruta_pct ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="min-h-[360px]">
        <CardHeader>
          <CardTitle className="text-base">Faturamento vs CMV por empresa</CardTitle>
          <p className="text-sm text-muted-foreground font-normal">
            Comparativo no mesmo período (DRE: receitas classificadas e custos na categoria CUSTO).
          </p>
        </CardHeader>
        <CardContent className="h-[300px]">
          {kpiQuery.isLoading ? (
            <div className="flex h-full flex-col justify-center gap-3 px-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Sem dados de empresas no grupo para o período.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={70} />
                <YAxis tickFormatter={(v) => formatCurrency(Number(v))} width={72} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="Faturamento" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="CMV" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-[120px]">
          <PendingTransfersWidget groupScope />
        </div>
        <div className="min-h-[120px]">
          <GroupBalancePanel />
        </div>
      </div>
    </div>
  );
}
